import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

export interface EntityOption {
  // Customers/entities are keyed by their numeric id, but some pickers only
  // have a name to work with (e.g. the Sales Invoice / Waybill customer lists
  // are derived from quotation rows). Allow either so a string label can
  // double as the key.
  id: number | string;
  name: string;
  // Optional second line shown under the name (phone, contact type, etc.).
  secondary?: string | null;
}

interface EntityComboboxProps {
  /**
   * Controlled display text. In free-text mode this is the live input value;
   * in select-only mode it is the committed selection shown when the menu is
   * closed.
   */
  value: string;
  /** The list of options to filter client-side. */
  options: EntityOption[];
  /** Fired when the user picks an option (mouse or keyboard). */
  onSelect: (option: EntityOption) => void;
  /**
   * When provided the combobox runs in FREE-TEXT mode (mirrors
   * `ProductCombobox`): the input is fully controlled by `value`, every
   * keystroke calls `onTextChange`, and arbitrary text is allowed. When
   * omitted the combobox runs in SELECT-ONLY mode: typing filters an internal
   * query, the committed `value` is only ever changed via `onSelect`, and a
   * blur/Escape discards the in-progress query. Pickers that previously used a
   * plain `<Select>` (no free text) omit this prop to preserve that behaviour.
   */
  onTextChange?: (value: string) => void;
  /**
   * Fired when the user picks the pinned "— None —" row to clear the field.
   * Callers should reset BOTH the display text and any associated id to
   * empty/null. When omitted the combobox falls back to a best-effort clear
   * (`onTextChange("")` in free-text mode, otherwise `onSelect` with an empty
   * option), but wiring this explicitly is preferred so id state is reset too.
   */
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Message shown when there are no options at all. */
  emptyText?: string;
  /** Forwarded to the underlying input so a <Label htmlFor> can target it. */
  id?: string;
  "aria-label"?: string;
  required?: boolean;
  invalid?: boolean;
}

// Cap rendered rows so a multi-thousand-row customer list doesn't mount
// thousands of DOM nodes. Mirrors ProductCombobox.
const MAX_VISIBLE = 200;

function matchesQuery(o: EntityOption, q: string) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (o.name?.toLowerCase().includes(needle)) return true;
  if (o.secondary?.toLowerCase().includes(needle)) return true;
  return false;
}

export default function EntityCombobox({
  value,
  options,
  onSelect,
  onTextChange,
  onClear,
  disabled,
  className,
  placeholder = "Click to choose, or type to filter…",
  emptyText = "No matches.",
  id,
  "aria-label": ariaLabel = "Entity",
  required = false,
  invalid = false,
}: EntityComboboxProps) {
  const freeText = typeof onTextChange === "function";

  const listboxId = useId();
  const optionIdPrefix = useId();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  // Prevents the dropdown from immediately reopening after a selection while
  // focus stays in the input. Cleared on the next text change / arrow key.
  const justPickedRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Only used in select-only mode — the free-text mode reads `value` directly.
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: "below" | "above";
  } | null>(null);

  // Text the filtering runs against.
  const filterText = freeText ? value.trim() : query.trim();

  // What the input element actually shows. Free-text mirrors `value`;
  // select-only shows the live query while open and the committed value once
  // closed.
  const inputValue = freeText ? value : open ? query : value;

  const filtered = useMemo(() => {
    const list = filterText
      ? options.filter((o) => matchesQuery(o, filterText))
      : options;
    return list.slice(0, MAX_VISIBLE);
  }, [options, filterText]);

  const totalMatches = useMemo(() => {
    if (!filterText) return options.length;
    return options.filter((o) => matchesQuery(o, filterText)).length;
  }, [options, filterText]);

  const positionDropdown = useCallback(() => {
    const trigger = inputRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const margin = 8;
    const gap = 4;
    const minHeight = 120;

    const spaceBelow = vh - rect.bottom - margin - gap;
    const spaceAbove = rect.top - margin - gap;

    const placement: "below" | "above" =
      spaceBelow >= minHeight || spaceBelow >= spaceAbove ? "below" : "above";

    const maxHeight = Math.max(
      minHeight,
      placement === "below" ? spaceBelow : spaceAbove,
    );

    const listRect = listRef.current?.getBoundingClientRect();
    const measured = listRect ? listRect.height : maxHeight;
    const usedHeight = Math.min(measured, maxHeight);

    const top =
      placement === "below" ? rect.bottom + gap : rect.top - usedHeight - gap;

    let left = rect.left;
    const maxLeft = Math.max(margin, vw - rect.width - margin);
    left = Math.min(Math.max(margin, left), maxLeft);

    // The menu is rendered with `position: fixed`, so its coordinates are
    // already viewport-relative — exactly what getBoundingClientRect() returns.
    // Do NOT add window.scrollX/scrollY here: that would push the menu off by
    // the page's scroll amount (it only made sense for position: absolute), so
    // on any scrolled page the dropdown landed below the visible area.
    setCoords({
      top,
      left,
      width: rect.width,
      maxHeight,
      placement,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    positionDropdown();
    const onScrollOrResize = () => positionDropdown();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    positionDropdown();
  }, [open, filtered, positionDropdown]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (inputRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLLIElement>(
      `[data-option-index="${activeIndex}"]`,
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  useEffect(() => {
    setActiveIndex(filtered.length ? 0 : -1);
  }, [filtered]);

  function pickOption(option: EntityOption) {
    onSelect(option);
    setOpen(false);
    setActiveIndex(-1);
    setQuery("");
    justPickedRef.current = true;
  }

  // Clear the field back to its empty/unselected state via the pinned
  // "— None —" row. Prefer the explicit onClear so the caller can also reset
  // its id; otherwise fall back to a best-effort clear.
  function clearSelection() {
    if (onClear) {
      onClear();
    } else if (freeText) {
      onTextChange?.("");
    } else {
      onSelect({ id: "", name: "" });
    }
    setOpen(false);
    setActiveIndex(-1);
    setQuery("");
    justPickedRef.current = true;
  }

  function openMenu() {
    if (disabled) return;
    if (justPickedRef.current) return;
    if (!freeText) setQuery("");
    setOpen(true);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      justPickedRef.current = false;
      if (!open) {
        if (!freeText) setQuery("");
        setOpen(true);
        return;
      }
      setActiveIndex((idx) =>
        filtered.length ? (idx + 1) % filtered.length : -1,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      justPickedRef.current = false;
      if (!open) {
        if (!freeText) setQuery("");
        setOpen(true);
        return;
      }
      setActiveIndex((idx) =>
        filtered.length ? (idx - 1 + filtered.length) % filtered.length : -1,
      );
    } else if (event.key === "Enter") {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        event.preventDefault();
        pickOption(filtered[activeIndex]!);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        setQuery("");
      }
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  }

  const activeId =
    open && activeIndex >= 0 ? `${optionIdPrefix}-${activeIndex}` : undefined;

  // In select-only mode show the committed selection as placeholder while the
  // user is typing a fresh query, so they still see what's currently chosen.
  const effectivePlaceholder =
    !freeText && open && value ? value : placeholder;

  return (
    <div className={cn("relative w-full", className)}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        autoComplete="off"
        spellCheck={false}
        value={inputValue}
        disabled={disabled}
        placeholder={effectivePlaceholder}
        onChange={(e) => {
          justPickedRef.current = false;
          if (freeText) {
            onTextChange?.(e.target.value);
          } else {
            setQuery(e.target.value);
          }
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          if (justPickedRef.current) return;
          if (!freeText) setQuery("");
          setOpen(true);
        }}
        onClick={openMenu}
        onKeyDown={handleKeyDown}
        className={cn(
          "h-10 w-full rounded-md border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          invalid
            ? "border-destructive/70 ring-1 ring-destructive/40 focus-visible:ring-destructive/70"
            : "border-input",
          disabled ? "opacity-60 cursor-not-allowed" : "",
        )}
      />
      {open && coords
        ? createPortal(
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={`${ariaLabel} suggestions`}
              data-placement={coords.placement}
              className="fixed z-[60] overflow-y-auto overscroll-contain rounded-md border bg-popover text-popover-foreground shadow-xl backdrop-blur"
              style={{
                top: coords.top,
                left: coords.left,
                width: coords.width,
                maxHeight: coords.maxHeight,
              }}
            >
              <li
                role="option"
                aria-selected={false}
                // onMouseDown so the click fires before the input blurs.
                onMouseDown={(event) => {
                  event.preventDefault();
                  clearSelection();
                }}
                className="cursor-pointer border-b px-3 py-2 text-xs italic text-muted-foreground hover:bg-muted"
              >
                — None —
              </li>
              {filtered.length === 0 && options.length === 0 ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  {emptyText}
                </li>
              ) : null}
              {filtered.length === 0 && options.length > 0 ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  No matches for “{filterText}”.
                </li>
              ) : null}
              {filtered.length > 0 ? (
                <li
                  className="sticky top-0 z-[1] bg-popover px-3 py-1 text-[10px] uppercase tracking-wide text-muted-foreground border-b"
                  aria-hidden
                >
                  {totalMatches > MAX_VISIBLE
                    ? `Showing ${MAX_VISIBLE} of ${totalMatches} — keep typing to narrow`
                    : `${totalMatches} result${totalMatches === 1 ? "" : "s"}`}
                </li>
              ) : null}
              {filtered.map((o, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <li
                    key={o.id}
                    id={`${optionIdPrefix}-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    data-option-index={idx}
                    onMouseEnter={() => setActiveIndex(idx)}
                    // onMouseDown so the click fires before the input blurs
                    // (which would otherwise close the listbox first).
                    onMouseDown={(event) => {
                      event.preventDefault();
                      pickOption(o);
                    }}
                    className={cn(
                      "flex items-center justify-between gap-3 px-3 py-2 text-xs cursor-pointer",
                      isActive
                        ? "bg-accent text-accent-foreground"
                        : "text-popover-foreground hover:bg-muted",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{o.name}</div>
                      {o.secondary ? (
                        <div className="truncate text-[10px] text-muted-foreground">
                          {o.secondary}
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
